// Inventory — scanner left, stock right. The camera scans CONTINUOUSLY
// while this mode is open (native BarcodeDetector; graceful message where
// unsupported): hold a barcode in front of the tablet and it books itself.
// IN/OUT toggle sets the direction; unknown barcodes open a name-it card
// that links the code to a CATALOGUE item — the shopping list is offered as
// quick picks, so a Picnic delivery is scanned pack by pack and the list
// empties without a single tick.

import { useCallback, useEffect, useRef, useState } from "react";
import useSWR from "swr";
import { PackageMinus, PackagePlus, ScanBarcode } from "lucide-react";

import { post, fetcher } from "@/api/client";
import { cn } from "@/lib/utils";
import { playClick, playSwitch } from "@/sounds";

interface StockRow {
  item_id: number;
  name: string;
  barcode: string | null;
  category: string | null;
  unit: string;
  location: string;
  quantity: number;
  next_best_before: string | null;
}

interface ScanResponse {
  status: "booked" | "needs_name";
  barcode?: string;
  item?: { name: string };
  quantity?: number;
  direction?: string;
  list_cleared?: boolean;
  suggestion?: { name: string; category: string | null } | null;
}

const LOCATIONS = ["fridge", "freezer", "pantry"] as const;
const SCAN_COOLDOWN_MS = 2500; // same barcode ignored while it lingers in frame

type BarcodeDetectorLike = {
  detect(source: CanvasImageSource): Promise<{ rawValue: string }[]>;
};

export default function Inventory() {
  const { data, mutate } = useSWR<{ stock: StockRow[]; expiring: StockRow[] }>(
    "/api/inventory",
    fetcher,
    { refreshInterval: 15000 },
  );

  const [direction, setDirection] = useState<"in" | "out">("in");
  const [location, setLocation] = useState<(typeof LOCATIONS)[number]>("fridge");
  const [lastScan, setLastScan] = useState<string | null>(null);
  const [nameIt, setNameIt] = useState<{ barcode: string; suggestion: string | null } | null>(null);
  const [cameraState, setCameraState] = useState<"starting" | "on" | "unsupported" | "denied">(
    () => ("BarcodeDetector" in window ? "starting" : "unsupported"),
  );

  const videoRef = useRef<HTMLVideoElement>(null);
  const cooldown = useRef<Map<string, number>>(new Map());
  // Latest-value refs so the long-lived scan loop reads the current toggle
  // without restarting the camera on every change.
  const directionRef = useRef(direction);
  const locationRef = useRef(location);
  useEffect(() => {
    directionRef.current = direction;
    locationRef.current = location;
  }, [direction, location]);

  const onBarcode = useCallback(
    async (code: string) => {
      const now = Date.now();
      const last = cooldown.current.get(code) ?? 0;
      if (now - last < SCAN_COOLDOWN_MS) return;
      cooldown.current.set(code, now);
      try {
        const res = await post<ScanResponse>("/api/scan", {
          barcode: code,
          direction: directionRef.current,
          location: locationRef.current,
        });
        if (res.status === "needs_name") {
          setNameIt({ barcode: code, suggestion: res.suggestion?.name ?? null });
        } else {
          playSwitch(directionRef.current === "in");
          setLastScan(
            `${res.item!.name} ${directionRef.current === "in" ? "+1" : "−1"} → ${res.quantity}${
              res.list_cleared ? " · off the list" : ""
            }`,
          );
          void mutate();
        }
      } catch {
        setLastScan(`scan failed (${code})`);
      }
    },
    [mutate],
  );

  // Continuous scan loop.
  useEffect(() => {
    if (!("BarcodeDetector" in window)) return;
    let stream: MediaStream | null = null;
    let timer: number | null = null;
    let cancelled = false;

    const Detector = (
      window as unknown as { BarcodeDetector: new (opts: { formats: string[] }) => BarcodeDetectorLike }
    ).BarcodeDetector;
    const detector = new Detector({
      formats: ["ean_13", "ean_8", "upc_a", "upc_e", "code_128"],
    });

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "environment" } })
      .then((s) => {
        if (cancelled) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        stream = s;
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          void videoRef.current.play();
        }
        setCameraState("on");
        timer = window.setInterval(async () => {
          const video = videoRef.current;
          if (!video || video.readyState < 2) return;
          try {
            const codes = await detector.detect(video);
            for (const c of codes) {
              if (c.rawValue) void onBarcode(c.rawValue);
            }
          } catch {
            // detector hiccup — next tick retries
          }
        }, 400);
      })
      .catch(() => setCameraState("denied"));

    return () => {
      cancelled = true;
      if (timer != null) window.clearInterval(timer);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [onBarcode]);

  const stock = data?.stock ?? [];
  const expiring = data?.expiring ?? [];
  const byLocation = LOCATIONS.map((loc) => ({
    loc,
    rows: stock.filter((r) => r.location === loc),
  }));

  return (
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4 md:flex-row md:overflow-hidden md:p-6">
      {/* ── scanner panel ── */}
      <section className="flex shrink-0 flex-col gap-3 md:w-80">
        <div className="relative aspect-[4/3] overflow-hidden rounded-[var(--radius-card)] border border-line-strong bg-panel">
          <video ref={videoRef} muted playsInline className="h-full w-full object-cover" />
          {cameraState !== "on" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4 text-center">
              <ScanBarcode className="size-8 text-dimmer" />
              <p className="text-xs text-dim">
                {cameraState === "starting" && "starting camera…"}
                {cameraState === "denied" && "Camera blocked — allow it in the browser settings."}
                {cameraState === "unsupported" &&
                  "This browser has no BarcodeDetector — scanning needs Chrome/Edge (or the kiosk browser)."}
              </p>
            </div>
          )}
          {lastScan && (
            <div className="absolute inset-x-2 bottom-2 rounded-lg bg-black/70 px-3 py-1.5 text-center font-mono text-xs text-[#f5f2e8]">
              {lastScan}
            </div>
          )}
        </div>

        {/* direction + location */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setDirection((d) => (d === "in" ? "out" : "in"));
              playClick();
            }}
            className={cn(
              "flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-full py-3 text-sm font-bold",
              "transition-[background-color,color] duration-300",
              direction === "in"
                ? "bg-brand text-primary-foreground"
                : "bg-[var(--status-error-tint)] text-status-error",
            )}
          >
            {direction === "in" ? (
              <>
                <PackagePlus className="size-4" /> scanning IN
              </>
            ) : (
              <>
                <PackageMinus className="size-4" /> scanning OUT
              </>
            )}
          </button>
        </div>
        <div className="flex gap-1.5">
          {LOCATIONS.map((loc) => (
            <button
              key={loc}
              type="button"
              onClick={() => {
                setLocation(loc);
                playClick();
              }}
              className={cn(
                "flex-1 cursor-pointer rounded-full px-3 py-2 text-xs font-semibold transition-colors",
                location === loc
                  ? "bg-panel text-foreground"
                  : "border border-line-strong text-dim hover:text-foreground",
              )}
            >
              {loc}
            </button>
          ))}
        </div>
      </section>

      {/* ── stock list ── */}
      <section className="flex min-w-0 flex-1 flex-col gap-4 md:overflow-y-auto">
        {expiring.length > 0 && (
          <div className="rounded-xl border border-status-warn/40 bg-[var(--fill-subtle)] px-4 py-3">
            <div className="mb-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-status-warn">
              use soon
            </div>
            <div className="flex flex-wrap gap-1.5">
              {expiring.map((r) => (
                <span key={`${r.item_id}-${r.location}`} className="rounded-full bg-panel px-3 py-1 text-xs">
                  {r.name} · {r.next_best_before}
                </span>
              ))}
            </div>
          </div>
        )}

        {byLocation.map(({ loc, rows }) => (
          <div key={loc}>
            <h2 className="mb-2 font-mono text-[11px] uppercase tracking-[0.14em] text-brand-ink">
              {loc}
            </h2>
            {rows.length === 0 ? (
              <p className="text-xs text-dimmer">empty</p>
            ) : (
              <ul className="divide-y divide-line rounded-xl border border-line bg-surface">
                {rows.map((r) => (
                  <li key={`${r.item_id}-${r.location}`} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                    <span className="min-w-0 flex-1 truncate">{r.name}</span>
                    {r.next_best_before && (
                      <span className="font-mono text-[11px] text-dim">{r.next_best_before}</span>
                    )}
                    <span className="font-mono text-xs text-dim">
                      {r.quantity} {r.unit === "piece" ? "×" : r.unit}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </section>

      {/* ── name-it card for unknown barcodes ── */}
      {nameIt && (
        <NameItCard
          barcode={nameIt.barcode}
          suggestion={nameIt.suggestion}
          location={location}
          onDone={(msg) => {
            setNameIt(null);
            if (msg) setLastScan(msg);
            void mutate();
          }}
        />
      )}
    </div>
  );
}

function NameItCard({
  barcode,
  suggestion,
  location,
  onDone,
}: {
  barcode: string;
  suggestion: string | null;
  location: string;
  onDone: (msg: string | null) => void;
}) {
  const [name, setName] = useState(suggestion ?? "");
  const [busy, setBusy] = useState(false);
  // The shopping list as quick picks: the pack in hand is most likely one of these.
  const { data: list } = useSWR<{ items: { name: string; checked: boolean }[] }>(
    "/api/menu/shopping",
    fetcher,
  );
  const picks = [...(list?.items ?? [])].sort((a, b) => Number(a.checked) - Number(b.checked));

  async function save(chosen: string) {
    const n = chosen.trim();
    if (!n || busy) return;
    setBusy(true);
    try {
      const r = await post<{ status: "linked" | "created"; item: { name: string }; list_cleared?: boolean }>(
        "/api/items",
        { barcode, name: n, location, book_in: true },
      );
      onDone(
        `${r.item.name} +1${r.status === "created" ? " (new item)" : ""}${
          r.list_cleared ? " · off the list" : ""
        }`,
      );
    } catch {
      onDone("could not save the item");
    }
  }

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/50 p-6">
      <div className="w-full max-w-sm rounded-[var(--radius-card)] border border-line-strong bg-popover p-5 shadow-[0_15px_40px_rgba(0,0,0,0.3)]">
        <h2 className="font-display text-2xl font-medium">New item</h2>
        <p className="mt-1 text-xs text-dim">
          Barcode <span className="font-mono">{barcode}</span> is not known yet — say what it is
          once, it is remembered forever. Pick it off the list, or name it.
        </p>
        {picks.length > 0 && (
          <div className="mt-3 flex max-h-40 flex-wrap gap-1.5 overflow-y-auto">
            {picks.map((p) => (
              <button
                key={p.name}
                type="button"
                disabled={busy}
                onClick={() => void save(p.name)}
                className={cn(
                  "cursor-pointer rounded-full border px-3 py-1 text-xs transition-colors disabled:opacity-35",
                  p.checked
                    ? "border-line text-dim hover:text-foreground"
                    : "border-brand-tint-border bg-brand-tint-08 text-brand-ink hover:bg-brand-tint-12",
                )}
              >
                {p.name}
              </button>
            ))}
          </div>
        )}
        <input
          autoFocus
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void save(name);
          }}
          placeholder="or type a name, e.g. Butter"
          className="mt-4 w-full rounded-lg border border-line-strong bg-transparent px-3 py-2.5 text-sm outline-none focus:border-brand-ink"
        />
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onDone(null)}
            className="cursor-pointer rounded-full px-4 py-2 text-xs font-semibold text-dim hover:text-foreground"
          >
            skip
          </button>
          <button
            type="button"
            disabled={!name.trim() || busy}
            onClick={() => void save(name)}
            className="cursor-pointer rounded-full bg-brand px-5 py-2 text-xs font-bold text-primary-foreground disabled:opacity-35"
          >
            save + book in
          </button>
        </div>
      </div>
    </div>
  );
}
